import React from "react";
import PropTypes from "prop-types";
import { Icon, Small, Popover } from "orcs-design-system";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";
import { useTagsExploreContext } from "src/pages/TagsExplore/providers/TagsExploreContext";
import { getChildCountInfo } from "src/util/childTagsCount";
import { useWorkspaceTagConfig } from "src/contexts/global/WorkspaceContext";

const CountContainer = styled(Small)`
  color: ${themeGet("colors.greyDark")};
  display: inline-flex;
  align-items: center;
  gap: ${themeGet("space.s", "8px")};
  padding: ${themeGet("space.xs", "4px")} ${themeGet("space.s", "8px")};
  border-radius: ${themeGet("radii.1", "4px")};
  transition: all 0.2s ease-in-out;
  cursor: default;

  &:hover {
    background-color: ${(props) =>
      props.hasTooltip
        ? themeGet("colors.greyLightest")
        : "rgba(0, 0, 0, 0.05)"};
    color: ${(props) =>
      props.hasTooltip
        ? themeGet("colors.greyDarkest")
        : themeGet("colors.greyDarkest")};
    transform: ${(props) => (props.hasTooltip ? "translateY(-1px)" : "none")};
  }
`;

/**
 * Context-aware component for displaying child tag counts
 * Only shows counts when in TagsExplore context
 * Accepts either pre-calculated label/tooltip or tag with childTags for auto-calculation
 */
const ChildCountDisplay = ({ tag = null, label = null, tooltip = null }) => {
  const tagsConfig = useWorkspaceTagConfig();
  const context = useTagsExploreContext();

  // Only show child counts when in TagsExplore context
  if (!context) {
    return null;
  }

  // Calculate child count info if tag is provided but label is not
  let displayLabel = label;
  let displayTooltip = tooltip;

  if (tag && !label) {
    // For individual tag cards, always show their specific child counts
    const childCountInfo = getChildCountInfo(tag?.childTags?.tags, tagsConfig);
    displayLabel = childCountInfo.label;
    displayTooltip = childCountInfo.tooltip;
  }

  // Don't render if no label (means no data or hidden)
  if (!displayLabel) {
    return null;
  }

  const content = (
    <CountContainer hasTooltip={!!displayTooltip}>
      <Icon icon={["fas", "level-up"]} size="sm" transform={{ rotate: 90 }} />
      {displayLabel}
    </CountContainer>
  );

  // Wrap in Popover if tooltip is provided (for mixed types)
  if (displayTooltip) {
    // Handle both array and string formats for backward compatibility
    const tooltipContent = Array.isArray(displayTooltip)
      ? displayTooltip.map((line) => <div key={line}>{line}</div>)
      : displayTooltip;

    return (
      <Popover
        direction="top"
        width="fit-content"
        text={tooltipContent}
        delay={300}
        trigger="hover"
      >
        {content}
      </Popover>
    );
  }

  return content;
};

ChildCountDisplay.propTypes = {
  tag: PropTypes.shape({
    childTags: PropTypes.shape({
      tags: PropTypes.array,
    }),
  }), // Tag with childTags for auto-calculation
  label: PropTypes.string, // Pre-calculated label: "0" or "2 Episodes" or "5" for mixed
  tooltip: PropTypes.oneOfType([
    PropTypes.string, // Legacy string format
    PropTypes.arrayOf(PropTypes.string), // Array of lines for proper multi-line display
  ]), // Multi-line breakdown for mixed types
};

export default ChildCountDisplay;
